import type { DeepReadonly, Ref } from 'vue'
import { select, selectAll } from '@/lib/bulk-selector'

export type MultiSelectItem = { id: number }

export interface UseMultipleSlidesSelect {
  selectedSlideIds: Readonly<Ref<number[]>>
  onClick(id: number): void
  onCtrlClick(id: number): void
  onShiftClick(id: number): void
  resetSlideSelection(): void
  setSelectedSlideIds(indices: number[]): void
}

export function useMultipleSlidesSelect({
  slides,
  currentSlideId,
  setCurrentSlideIndex,
  suppressReset = false,
}: {
  slides: DeepReadonly<Ref<MultiSelectItem[]>>
  currentSlideId: Ref<number>
  setCurrentSlideIndex(id: number): void
  suppressReset?: boolean
}): UseMultipleSlidesSelect {
  const selectedSlideIds = ref<number[]>(Number.isFinite(currentSlideId.value) ? [currentSlideId.value] : [])
  const slideIds = computed<number[]>(() => slides.value.map(s => s.id))

  watch(
    [currentSlideId, slides],
    ([currentSlideId]) => {
      if (!selectedSlideIds.value.includes(currentSlideId) && !suppressReset) {
        resetSlideSelection()
      }
    },
    { flush: 'post' }
  )

  function onCtrlClick(id: number) {
    onClick(id)
    _syncCurrentSlideIndex()
  }

  function onClick(id: number) {
    const [_, nextSelectedIds] = select(selectedSlideIds.value, currentSlideId.value, id)
    selectedSlideIds.value = nextSelectedIds.length ? nextSelectedIds : [id]
  }

  function onShiftClick(id: number) {
    selectedSlideIds.value = selectAll({
      selectedIds: selectedSlideIds.value,
      selectedId: currentSlideId.value,
      ids: slideIds.value,
      id: id,
    })
    _syncCurrentSlideIndex()
  }

  function resetSlideSelection() {
    selectedSlideIds.value = Number.isFinite(currentSlideId.value) ? [currentSlideId.value] : []
  }

  function setSelectedSlideIds(ids: number[]): void {
    selectedSlideIds.value = ids
    _syncCurrentSlideIndex(ids)
  }

  function _syncCurrentSlideIndex(selected = selectedSlideIds.value) {
    const isCurrentSelected = selected.includes(currentSlideId.value)
    if (!isCurrentSelected) {
      setCurrentSlideIndex(selected[0])
    }
  }

  return {
    selectedSlideIds,
    setSelectedSlideIds,
    onClick,
    onCtrlClick,
    onShiftClick,
    resetSlideSelection,
  }
}
